/**
 * ChatPanel Component
 * Main chat interface with message history, input, and admin controls
 * Fixed position at bottom-right, above FAB
 * Theme-aware with Tailwind dark: prefix classes
 */

import { useEffect, useRef, useState } from 'react';
import { ChatPanelProps } from '../../types/liveChat';
import { cn } from '../../utils/cn';
import { ChatMessage } from './ChatMessage';
import { ChatMessageInput } from './ChatMessageInput';
import { TypingIndicator } from './TypingIndicator';
import { X, Star } from 'lucide-react';

export const ChatPanel: React.FC<Omit<ChatPanelProps, 'theme'>> = ({
  conversation,
  messages,
  inputValue,
  onInputChange,
  onSend,
  onClose,
  isTyping,
  isLoading = false
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [ratingFeedback, setRatingFeedback] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);

  /**
   * Auto-scroll to latest message
   */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  /**
   * Show rating modal when conversation is resolved
   */
  useEffect(() => {
    if (conversation?.status === 'resolved' && !conversation.ratings) {
      setShowRatingModal(true);
    }
  }, [conversation?.status, conversation?.ratings]);

  /**
   * Handle rating submission
   */
  const handleRateConversation = () => {
    if (rating === null) return;
    // Rating is handled by parent component
    onSend(`[RATING: ${rating}/5]${ratingFeedback ? ` - ${ratingFeedback}` : ''}`);
    setShowRatingModal(false);
    setRating(null);
    setRatingFeedback('');
  };

  /**
   * Empty state
   */
  if (!conversation) {
    return (
      <div className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 w-[calc(100vw-32px)] sm:w-96 max-h-[calc(100vh-200px)] sm:max-h-96 rounded-2xl shadow-lg flex flex-col overflow-hidden z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overscroll-contain">
        {/* Header - Theme-aware with Tailwind dark: prefix */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-gray-900 dark:text-white font-semibold">Live Chat</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Get instant help from our support team</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            aria-label="Close chat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Empty state content */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 overscroll-contain">
          <div className="text-center">
            <p className="text-sm font-medium mb-4 text-gray-700 dark:text-gray-300">
              Start a conversation
            </p>
            <p className="text-xs mb-6 text-gray-500 dark:text-gray-400">
              Our support team is here to help. Send your first message to begin.
            </p>
          </div>
        </div>

        {/* Input */}
        <ChatMessageInput
          value={inputValue}
          onChange={onInputChange}
          onSend={onSend}
          disabled={false}
        />
      </div>
    );
  }

  /**
   * Active conversation
   */
  return (
    <div className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 w-[calc(100vw-32px)] sm:w-96 max-h-[calc(100vh-200px)] sm:max-h-96 rounded-2xl shadow-lg flex flex-col overflow-hidden z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overscroll-contain">
      {/* Header with status */}
      <div className="px-4 py-3 flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Support Team
            </h3>
            <div className={cn(
              'w-2 h-2 rounded-full',
              conversation?.status === 'assigned' ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-600'
            )} />
          </div>
          <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
            {conversation?.status === 'assigned' ? 'Admin is here' : 'Waiting to be assigned...'}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:opacity-80 transition-opacity text-gray-600 dark:text-gray-400"
          aria-label="Close chat"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white dark:bg-gray-800 overscroll-contain">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No messages yet. Send a message to get started.
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage
                key={message._id}
                message={message}
              />
            ))}
            {isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Rating modal */}
      {showRatingModal && (
        <div className="absolute inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center rounded-2xl z-50">
          <div className="bg-white dark:bg-gray-700 rounded-lg p-4 max-w-xs w-[calc(100vw-64px)] sm:w-auto border border-gray-200 dark:border-gray-600">
            <p className="font-semibold mb-4 text-gray-900 dark:text-white">
              How was your experience?
            </p>

            {/* Rating stars */}
            <div className="flex gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={cn(
                      'w-6 h-6 cursor-pointer',
                      rating && rating >= star
                        ? 'fill-primary-500 text-primary-500'
                        : 'text-gray-300 dark:text-gray-500'
                    )}
                  />
                </button>
              ))}
            </div>

            {/* Feedback textarea */}
            <textarea
              value={ratingFeedback}
              onChange={(e) => setRatingFeedback(e.target.value)}
              placeholder="Any comments? (optional)"
              className="w-full p-2 rounded text-sm mb-4 resize-none bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              rows={2}
            />

            {/* Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowRatingModal(false)}
                className="flex-1 px-3 py-2 rounded text-sm font-medium bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-700"
              >
                Skip
              </button>
              <button
                onClick={handleRateConversation}
                disabled={rating === null}
                className={cn(
                  'flex-1 px-3 py-2 rounded text-sm font-medium text-white',
                  rating !== null
                    ? 'bg-primary-600 hover:bg-primary-700'
                    : 'bg-gray-400 cursor-not-allowed'
                )}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <ChatMessageInput
        value={inputValue}
        onChange={onInputChange}
        onSend={onSend}
        disabled={isLoading || conversation?.status === 'closed'}
        isLoading={isLoading}
      />
    </div>
  );
};